import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="glass-card p-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Terms and Conditions
          </h1>
          <p className="text-gray-600 mb-8">Last Updated: 12.03.2025</p>

          <div className="prose prose-gray prose-lg max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">By accessing or using DECIDE ("the App"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use the App.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed">DECIDE is a decision-support tool that helps users organize their thoughts using the Logic, Universe, and Vibe (LUV) framework. The App provides reflective guidance to support your decision-making process.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Not Professional Advice</h2>
            <p><strong>IMPORTANT:</strong> DECIDE provides recommendations for informational and reflective purposes only. The App does not provide:</p>
            <ul>
              <li>Legal advice</li>
              <li>Medical advice</li>
              <li>Financial advice</li>
              <li>Professional counseling or therapy</li>
              <li>Any form of licensed professional guidance</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">The recommendations generated by DECIDE are based solely on the information you provide and are intended to help you reflect on your own inner knowing. They should not be considered professional advice or a substitute for consultation with qualified professionals.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. User Responsibility and Assumption of Risk</h2>
            <p><strong>You acknowledge and agree that:</strong></p>
            <p className="text-gray-700 leading-relaxed">a) <strong>All decisions are your own.</strong> You are solely responsible for any decisions you make, whether or not influenced by using DECIDE.</p>
            <p className="text-gray-700 leading-relaxed">b) <strong>Use at your own risk.</strong> Your use of the App and any actions you take based on recommendations from the App are entirely at your own risk.</p>
            <p className="text-gray-700 leading-relaxed">c) <strong>No guarantees.</strong> We make no guarantees, warranties, or representations about the accuracy, completeness, or usefulness of any recommendations provided by the App.</p>
            <p className="text-gray-700 leading-relaxed">d) <strong>Independent judgment required.</strong> You should exercise your own independent judgment and, where appropriate, seek professional advice before making important decisions.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Limitation of Liability</h2>
            <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong></p>
            <p className="text-gray-700 leading-relaxed">a) DECIDE, its owners, operators, employees, and affiliates shall not be liable for any direct, indirect, incidental, consequential, special, or punitive damages arising from:</p>
            <ul>
              <li>Your use of or inability to use the App</li>
              <li>Any decisions you make based on App recommendations</li>
              <li>Any actions you take or fail to take based on App content</li>
              <li>Any errors, omissions, or inaccuracies in App recommendations</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">b) This limitation applies regardless of the legal theory (contract, tort, negligence, strict liability, or otherwise), even if we have been advised of the possibility of such damages.</p>
            <p className="text-gray-700 leading-relaxed">c) Some jurisdictions do not allow the exclusion or limitation of liability for consequential or incidental damages. In such jurisdictions, our liability is limited to the maximum extent permitted by law.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. User Content and Input</h2>
            <p className="text-gray-700 leading-relaxed">a) You retain ownership of any information, thoughts, or content you input into the App ("User Content").</p>
            <p className="text-gray-700 leading-relaxed">b) By using the App, you grant DECIDE a non-exclusive, worldwide, royalty-free license to use, store, and process your User Content solely for the purposes of:</p>
            <ul>
              <li>Providing the service to you</li>
              <li>Improving the App and its AI systems</li>
              <li>Training and refining our recommendation algorithms</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Account Security</h2>
            <p className="text-gray-700 leading-relaxed">You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Prohibited Uses</h2>
            <p className="text-gray-700 leading-relaxed">You agree not to:</p>
            <ul>
              <li>Use the App for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to the App or its systems</li>
              <li>Interfere with or disrupt the App's functionality</li>
              <li>Use automated systems (bots, scrapers) to access the App without permission</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Modifications to Service</h2>
            <p className="text-gray-700 leading-relaxed">We reserve the right to modify, suspend, or discontinue the App at any time. Continued use of the App after changes constitutes acceptance of the modified terms.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">These Terms shall be governed by and construed in accordance with the laws of New Jersey, without regard to its conflict of law provisions.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed">For questions about these Terms, please contact: <a href="mailto:decide@elev8tek.com" className="text-purple-600 hover:text-purple-700">decide@elev8tek.com</a></p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Acknowledgment</h2>
            <p className="text-gray-700 leading-relaxed">By using DECIDE, you acknowledge that:</p>
            <ul>
              <li>You have read and understood these Terms</li>
              <li>You agree to be bound by these Terms</li>
              <li>You understand that all recommendations are for reflection only</li>
              <li>You accept full responsibility for your decisions and actions</li>
            </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
